import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, CreditCard, FileText, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton } from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { money, price as fmtPrice, shortDate } from "../lib/format";
import { useTranslation } from "react-i18next";

export default function Billing() {
  const { t } = useTranslation();
  const { user, isPaid, patchUser } = useAuth();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [winBack, setWinBack] = useState(null);

  useEffect(() => {
    Promise.all([api.billingConfig(), api.subscription(), api.invoices()])
      .then(([c, s, i]) => { setConfig(c.data); setSub(s.data); setInvoices(i.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Returning from Stripe Checkout. The redirect is only a hint — entitlement comes
  // from the webhook, so we refetch /me rather than trusting the query string.
  useEffect(() => {
    const status = params.get("status");
    if (status === "success") {
      toast.success(t("billing.thanks"));
      api.me().then((r) => patchUser(r.data)).catch(() => {});
    } else if (status === "cancelled") {
      toast.info(t("billing.cancelled"));
    }
  }, [params, patchUser, t]);

  const checkout = async (plan) => {
    setBusy(true);
    try {
      const { data } = await api.startCheckout(plan);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
      setBusy(false);
    }
  };

  const openCancel = async () => {
    setCancelOpen(true); setWinBack(null); setReason(""); setComment("");
    if (!reasons.length) {
      try { const { data } = await api.cancelReasons(); setReasons(data.reasons); } catch {}
    }
  };

  // Capture the reason *before* handing off to Stripe — asking afterwards gets
  // almost no responses, and the reason is what makes a targeted offer possible.
  const submitCancel = async () => {
    if (!reason) return;
    setBusy(true);
    try {
      const { data } = await api.cancelFeedback({ reason, comment });
      if (data.win_back) { setWinBack(data.win_back); }
      else { await portal(); }
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  const portal = async () => {
    setBusy(true);
    try {
      const { data } = await api.billingPortal();
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
      setBusy(false);
    }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-32" /></div>;

  const features = ["billing.f1", "billing.f2", "billing.f3", "billing.f4"];

  return (
    <div className="space-y-6">
      <div>
        <span className="label">{t("billing.subtitle")}</span>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">
          {t("billing.title")}
        </h1>
      </div>

      {/* Two cards, priced from the server. The plan a member is on is derived from
          the Stripe price on their subscription, so what is offered here and what is
          granted afterwards come from the same source and cannot drift. */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="billing-plans">
        {[
          { key: "pro", features: ["plans.featurePro1", "plans.featurePro2", "plans.featurePro3"] },
          { key: "full", features: ["plans.featureFull1", "plans.featureFull2", "plans.featureFull3"] },
        ].map(({ key, features: feats }) => {
          const info = config?.plans?.[key];
          const isCurrent = (user?.plan || "free") === key;
          const highlight = key === "full";
          return (
            <Panel key={key} testid={`plan-card-${key}`}
                   className={`card p-6 flex flex-col ${highlight ? "border-brand-300 ring-1 ring-brand-200" : ""}`}>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-heading font-semibold tracking-tight">{t(`plans.${key}`)}</h2>
                {isCurrent && (
                  <span className="pill bg-emerald-50 text-emerald-600 flex items-center gap-1">
                    <Check size={12} /> {t("plans.current")}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">{t(`plans.${key}Tagline`)}</p>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="text-3xl font-heading font-semibold tabular">
                  {/* Straight from the server, with no default to fall back on. A
                      literal here would be a second price that only shows up when the
                      request is slow — which is exactly when it would be wrong. */}
                  {typeof info?.gross_eur === "number"
                    ? fmtPrice(info.gross_eur, config?.currency || "EUR")
                    : <span className="inline-block w-20 h-7 rounded-lg bg-slate-100 animate-pulse" />}
                </span>
                <span className="text-sm text-slate-400">{t("plans.perMonth")}</span>
              </div>
              <ul className="mt-4 space-y-2 flex-1">
                {feats.map((k) => (
                  <li key={k} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check size={15} className="text-emerald-500 shrink-0 mt-0.5" /> {t(k)}
                  </li>
                ))}
              </ul>
              {!isCurrent && (
                <button
                  data-testid={`checkout-${key}`}
                  className={`${highlight ? "btn-primary" : "btn-outline"} justify-center mt-5`}
                  onClick={() => checkout(key)}
                  disabled={busy || !config?.configured || !info?.available}
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  {info?.available === false ? t("plans.unavailable") : t("plans.choose", { plan: t(`plans.${key}`) })}
                </button>
              )}
            </Panel>
          );
        })}
      </div>

      <Panel className="card p-6" testid="billing-plan">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-heading font-semibold tracking-tight">{t("billing.planName")}</h2>
              {isPaid && (
                <span className="pill bg-emerald-50 text-emerald-600 flex items-center gap-1">
                  <Check size={12} /> {t("billing.active")}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">{t("billing.planDesc")}</p>
          </div>
        </div>

        {config && !config.configured && (
          <div className="mt-5 rounded-xl bg-amber-50 border border-amber-100 p-3.5 text-sm text-amber-800"
               data-testid="billing-not-configured">
            {t("billing.notConfigured")}
          </div>
        )}

        {config?.configured && (
          <p className="mt-4 text-[11px] text-slate-400 flex items-center gap-1.5">
            <ShieldCheck size={13} /> {t("billing.vatNote", { rate: config.vat_rate_pct })}
          </p>
        )}
      </Panel>

      {sub?.has_customer && (
        <Panel className="card p-6" testid="billing-manage">
          <h3 className="font-heading font-semibold tracking-tight text-lg">{t("billing.manageTitle")}</h3>
          <div className="mt-3 space-y-1.5 text-sm text-slate-600">
            <div className="flex justify-between">
              <span className="text-slate-400">{t("billing.status")}</span>
              <span className="font-medium">{sub.status || "—"}</span>
            </div>
            {sub.current_period_end && (
              <div className="flex justify-between">
                <span className="text-slate-400">
                  {sub.cancel_at_period_end ? t("billing.endsOn") : t("billing.renewsOn")}
                </span>
                <span className="font-medium tabular">{shortDate(sub.current_period_end)}</span>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button className="btn-outline justify-center" onClick={portal} disabled={busy} data-testid="portal-btn">
              {t("billing.manageBtn")}
            </button>
            {isPaid && (
              <button className="btn-ghost justify-center text-slate-500" onClick={openCancel}
                      data-testid="cancel-btn">
                {t("billing.cancel")}
              </button>
            )}
          </div>
        </Panel>
      )}

      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setCancelOpen(false)} />
          <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl p-5 sm:p-6 space-y-4"
               data-testid="cancel-modal">
            {winBack ? (
              <>
                <h3 className="font-heading font-semibold text-xl">{t(`billing.winBack.${winBack}.title`)}</h3>
                <p className="text-sm text-slate-600">{t(`billing.winBack.${winBack}.body`)}</p>
                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                  <button className="btn-ghost justify-center" onClick={portal} data-testid="cancel-anyway">
                    {t("billing.cancelAnyway")}
                  </button>
                  <button className="btn-primary justify-center" onClick={() => setCancelOpen(false)}
                          data-testid="keep-subscription">
                    {t("billing.keepIt")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-heading font-semibold text-xl">{t("billing.cancelTitle")}</h3>
                <p className="text-sm text-slate-600">{t("billing.cancelBody")}</p>
                <div className="space-y-2">
                  {reasons.map((r) => (
                    <label key={r.key} className="flex items-center gap-2.5 cursor-pointer text-sm">
                      <input type="radio" name="cancel-reason" value={r.key} checked={reason === r.key}
                             onChange={() => setReason(r.key)} data-testid={`reason-${r.key}`} />
                      {r.label}
                    </label>
                  ))}
                </div>
                <textarea className="input min-h-[80px] resize-none text-sm" value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder={t("billing.cancelCommentPlaceholder")} data-testid="cancel-comment" />
                <div className="flex flex-wrap justify-end gap-2">
                  <button className="btn-ghost" onClick={() => setCancelOpen(false)}>{t("common.cancel")}</button>
                  <button className="btn-primary" onClick={submitCancel} disabled={!reason || busy}
                          data-testid="cancel-continue">
                    {t("billing.cancelContinue")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Panel className="card p-6" testid="billing-invoices">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={18} className="text-brand-500" />
          <h3 className="font-heading font-semibold tracking-tight text-lg">{t("billing.invoices")}</h3>
        </div>
        {invoices.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">{t("billing.noInvoices")}</p>
        ) : (
          <div className="space-y-2">
            {invoices.map((inv) => (
              <a
                key={inv.number}
                href={inv.pdf_url || inv.hosted_invoice_url || "#"}
                target="_blank" rel="noreferrer"
                data-testid={`invoice-${inv.number}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-4 py-3 hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm text-slate-800 tabular">{inv.number}</div>
                  <div className="text-xs text-slate-400">{shortDate(inv.issued_at)}</div>
                </div>
                <div className="text-right shrink-0 text-sm">
                  <div className="font-medium tabular">{money(inv.total_amount, inv.currency)}</div>
                  <div className="text-[11px] text-slate-400 tabular">
                    {t("billing.inclVat", { amount: money(inv.tax_amount, inv.currency), rate: inv.vat_rate_pct })}
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
